/* PATCH 41 — formulaires de devis (patchs et stickers).
   - Vérifie les champs et les fichiers avant l’envoi, avec des messages clairs.
   - Mode « service » : envoie champs + fichiers au service configuré et n’affiche
     la confirmation qu’après une réponse positive de ce service.
   - Mode « native » : formulaire de contact Shopify (sans fichiers) ; les saisies
     sont conservées si Shopify renvoie une erreur. */
(() => {
  'use strict';
  const p41t = (k, f) => { const s = window.PATCH41_STRINGS; return (s && s[k]) || f; };
  const fill = (text, values) => Object.keys(values).reduce((out, k) => out.split(`[${k}]`).join(values[k]), text);
  const ACCEPT = { pdf: ['application/pdf'], png: ['image/png'], jpg: ['image/jpeg', 'image/pjpeg'], jpeg: ['image/jpeg', 'image/pjpeg'], heic: ['image/heic', 'image/heif'], heif: ['image/heif', 'image/heic'], webp: ['image/webp'] };
  const FORMATS = p41t('quote_formats', 'photo (JPG, PNG, HEIC, WebP) ou PDF');
  /* Photos lourdes (téléphone) : réduites à 2560 px avant l’envoi pour rester sous les limites
     et accélérer l’envoi. Si le navigateur ne sait pas les lire (HEIC hors Safari), l’original est gardé. */
  const SHRINK_FROM = 3.5 * 1024 * 1024;
  const MAX_SIDE = 2560;
  const shrink = async file => {
    const type = (file.type || '').toLowerCase();
    if (!/^image\/(jpeg|pjpeg|png|webp)$/.test(type) || file.size <= SHRINK_FROM || !window.createImageBitmap) return file;
    try {
      const bitmap = await createImageBitmap(file);
      const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bitmap.width * scale);
      canvas.height = Math.round(bitmap.height * scale);
      canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      if (bitmap.close) bitmap.close();
      const isPng = type === 'image/png';
      const blob = await new Promise(resolve => canvas.toBlob(resolve, isPng ? 'image/png' : 'image/jpeg', 0.86));
      if (!blob || blob.size >= file.size) return file;
      const name = isPng ? file.name : file.name.replace(/\.(jpe?g|webp)$/i, '') + '.jpg';
      return new File([blob], name, { type: blob.type, lastModified: file.lastModified });
    } catch (e) { return file; }
  };
  const MB = 1024 * 1024;
  const fmtSize = bytes => bytes < MB
    ? fill(p41t('quote_size_kb', '[n] Ko'), { n: Math.max(1, Math.round(bytes / 1024)) })
    : fill(p41t('quote_size_mb', '[n] Mo'), { n: (bytes / MB).toFixed(1).replace('.', p41t('quote_decimal_sep', ',')) });
  const keyOf = el => {
    const n = (el.name || '').toLowerCase();
    if (n.includes('email')) return 'email';
    if (n.includes('quantit')) return 'qty';
    if (n.includes('description') || n.includes('[body]')) return 'desc';
    if (n.includes('date')) return 'date';
    if (n.endsWith('-nom') || n.endsWith('-fullname') || n.includes('[name]')) return 'name';
    return 'other';
  };

  function init(form) {
    if (form.dataset.ready) return;
    form.dataset.ready = 'true';
    form.noValidate = true;
    const mode = form.dataset.mode;
    const minQty = parseInt(form.dataset.minQty || '1', 10);
    const kind = form.dataset.kind || 'patch';
    const unit = kind === 'sticker' ? p41t('quote_unit_stickers', 'stickers') : p41t('quote_unit_patches', 'patchs');
    const maxFiles = parseInt(form.dataset.maxFiles || '5', 10);
    const maxFile = parseFloat(form.dataset.maxFileMb || '10') * MB;
    const maxTotal = parseFloat(form.dataset.maxTotalMb || '20') * MB;
    const summary = form.querySelector('[data-error-summary]');
    const summaryList = form.querySelector('[data-error-list]');
    const summaryTitle = form.querySelector('[data-error-title]');
    const submit = form.querySelector('[data-submit]');
    const submitLabel = form.querySelector('[data-submit-label]');
    const submitText = submitLabel ? submitLabel.textContent : '';
    const storageKey = `patch41-quote-${kind}`;
    let files = [];

    /* Champ « Autre, à préciser » */
    form.querySelectorAll('fieldset[data-group-name]').forEach(group => {
      const reveal = group.querySelector('[data-reveal]');
      if (!reveal) return;
      group.addEventListener('change', () => {
        const trigger = group.querySelector('[data-reveal-trigger]');
        reveal.hidden = !(trigger && trigger.checked);
      });
    });

    /* Messages d’erreur */
    const errorEl = field => {
      const container = field.closest('[data-field]') || field.closest('[data-file-field]');
      return container ? container.querySelector('[data-error]') : null;
    };
    const setError = (field, message) => {
      const el = errorEl(field);
      if (field.matches('input,select,textarea')) field.setAttribute('aria-invalid', message ? 'true' : 'false');
      if (el) {
        el.hidden = !message;
        el.innerHTML = message ? '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 2.5 20h19zM12 9.5v5M12 17v.5"/></svg>' : '';
        if (message) el.appendChild(document.createTextNode(message));
      }
    };

    const validateField = el => {
      if (!el.name || el.type === 'hidden' || el.name === '_gotcha' || el.type === 'file' || el.closest('[hidden]')) return '';
      const value = (el.value || '').trim();
      const key = keyOf(el);
      if (el.required && !value) {
        const custom = el.dataset.msgRequired;
        if (custom) return custom;
        return { name: p41t('quote_req_name', 'Indiquez votre nom et prénom.'), email: p41t('quote_req_email', 'Indiquez votre adresse e-mail.'), qty: p41t('quote_req_qty', 'Indiquez la quantité souhaitée.'), desc: kind === 'contact' ? p41t('quote_req_message', 'Écrivez votre message.') : p41t('quote_req_desc', 'Décrivez votre projet en quelques mots.') }[key] || p41t('quote_req_field', 'Ce champ est obligatoire.');
      }
      if (!value) return '';
      if (el.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) return p41t('quote_email_invalid', 'Cette adresse e-mail ne semble pas complète (ex. nom@domaine.fr).');
      if (el.type === 'number') {
        if (!/^\d+$/.test(value)) return p41t('quote_qty_integer', 'Indiquez un nombre entier, sans virgule.');
        if (parseInt(value, 10) < minQty) return minQty > 1 ? fill(p41t('quote_qty_min', 'La quantité minimale est de [n] [unit].'), { n: minQty, unit }) : p41t('quote_qty_min_one', 'Indiquez une quantité d’au moins 1.');
      }
      if (el.type === 'date' && el.min && value < el.min) return p41t('quote_date_future', 'Choisissez une date à venir.');
      if (el.maxLength > 0 && value.length > el.maxLength) return fill(p41t('quote_too_long', 'Ce texte est trop long ([n] caractères maximum).'), { n: el.maxLength });
      return '';
    };

    const fieldsToCheck = () => Array.from(form.querySelectorAll('input,select,textarea')).filter(el => el.name && el.type !== 'radio' && el.type !== 'hidden');

    fieldsToCheck().forEach(el => {
      el.addEventListener('blur', () => { if (el.value) setError(el, validateField(el)); });
      el.addEventListener('input', () => { if (el.getAttribute('aria-invalid') === 'true') setError(el, validateField(el)); });
    });

    /* Fichiers */
    const fileField = form.querySelector('[data-file-field]');
    const fileInput = form.querySelector('[data-file-input]');
    const fileList = form.querySelector('[data-file-list]');
    const fileTotal = form.querySelector('[data-file-total]');
    const fileName = fileInput ? fileInput.name : '';
    const renderFiles = () => {
      if (!fileList) return;
      fileList.innerHTML = '';
      files.forEach((file, index) => {
        const li = document.createElement('li');
        li.className = 'file-item';
        li.innerHTML = '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3h8l4 4v14H6zM14 3v4h4"/></svg><span class="file-name"></span><span class="file-size"></span><button type="button" class="file-remove"><svg class="icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5 5 19"/></svg></button>';
        if (/^image\/(jpeg|pjpeg|png|webp|gif)$/.test(file.type || '')) {
          const thumb = document.createElement('img');
          thumb.className = 'file-thumb';
          thumb.alt = '';
          thumb.src = URL.createObjectURL(file);
          thumb.onload = thumb.onerror = () => URL.revokeObjectURL(thumb.src);
          li.querySelector('.icon').replaceWith(thumb);
        }
        li.querySelector('.file-name').textContent = file.name;
        li.querySelector('.file-size').textContent = fmtSize(file.size);
        const btn = li.querySelector('.file-remove');
        btn.setAttribute('aria-label', fill(p41t('quote_file_remove', 'Retirer [name]'), { name: file.name }));
        btn.addEventListener('click', () => {
          files.splice(index, 1);
          renderFiles();
          setError(fileInput, '');
          window.PATCH41 && PATCH41.announce && PATCH41.announce(fill(p41t('quote_file_removed', '[name] retiré.'), { name: file.name }));
          (fileList.querySelector('.file-remove') || fileInput).focus();
        });
        fileList.appendChild(li);
      });
      const total = files.reduce((sum, f) => sum + f.size, 0);
      if (fileTotal) {
        fileTotal.hidden = files.length === 0;
        fileTotal.textContent = fill(files.length > 1 ? p41t('quote_files_total_other', '[count] fichiers · [size] sur [max] possibles') : p41t('quote_files_total_one', '[count] fichier · [size] sur [max] possibles'), { count: files.length, size: fmtSize(total), max: fmtSize(maxTotal) });
      }
    };
    let preparing = 0;
    const addFiles = async list => {
      const problems = [];
      const picked = Array.from(list);
      preparing += 1;
      if (fileTotal && picked.length) { fileTotal.hidden = false; fileTotal.textContent = p41t('quote_files_preparing', 'Préparation des fichiers…'); }
      if (submit) submit.disabled = true;
      for (const original of picked) {
        const ext = (original.name.split('.').pop() || '').toLowerCase();
        const typeOk = ACCEPT[ext] && (!original.type || ACCEPT[ext].includes(original.type));
        if (!typeOk) { problems.push(fill(p41t('quote_file_type', '« [name] » n’est pas accepté : envoyez une [formats].'), { name: original.name, formats: FORMATS })); continue; }
        if (original.size === 0) { problems.push(fill(p41t('quote_file_empty', '« [name] » est vide.'), { name: original.name })); continue; }
        const key = `${original.name}|${original.size}`;
        if (files.some(f => f.__key === key)) continue;
        if (files.length >= maxFiles) { problems.push(fill(p41t('quote_file_max_count', '[max] fichiers maximum : « [name] » n’a pas été ajouté.'), { max: maxFiles, name: original.name })); continue; }
        const file = await shrink(original);
        file.__key = key;
        if (file.size > maxFile) { problems.push(fill(p41t('quote_file_too_big', '« [name] » dépasse [size].'), { name: original.name, size: fmtSize(maxFile) })); continue; }
        const total = files.reduce((sum, f) => sum + f.size, 0) + file.size;
        if (total > maxTotal) { problems.push(fill(p41t('quote_file_total_over', '« [name] » ferait dépasser le total autorisé de [size].'), { name: original.name, size: fmtSize(maxTotal) })); continue; }
        files.push(file);
      }
      preparing -= 1;
      if (submit && !preparing) submit.disabled = false;
      renderFiles();
      setError(fileInput, problems.join(' '));
      if (!problems.length && files.length && window.PATCH41 && PATCH41.announce) PATCH41.announce(fill(files.length > 1 ? p41t('quote_files_selected_other', '[count] fichiers sélectionnés.') : p41t('quote_files_selected_one', '[count] fichier sélectionné.'), { count: files.length }));
    };
    if (fileInput) {
      fileInput.addEventListener('change', () => { const picked = Array.from(fileInput.files); fileInput.value = ''; addFiles(picked); });
      const zone = form.querySelector('[data-dropzone]');
      ['dragenter', 'dragover'].forEach(t => zone.addEventListener(t, e => { e.preventDefault(); zone.classList.add('is-over'); }));
      ['dragleave', 'drop'].forEach(t => zone.addEventListener(t, e => { e.preventDefault(); zone.classList.remove('is-over'); }));
      zone.addEventListener('drop', e => { if (e.dataTransfer && e.dataTransfer.files) addFiles(Array.from(e.dataTransfer.files)); });
    }

    /* Résumé des erreurs */
    const showSummary = (title, items) => {
      summaryTitle.textContent = title;
      summaryList.innerHTML = '';
      items.forEach(({ el, message }) => {
        const li = document.createElement('li');
        if (el && el.id) {
          const a = document.createElement('a');
          a.href = `#${el.id}`;
          a.textContent = message;
          a.addEventListener('click', e => { e.preventDefault(); el.focus(); el.scrollIntoView({ block: 'center' }); });
          li.appendChild(a);
        } else li.textContent = message;
        summaryList.appendChild(li);
      });
      summary.hidden = false;
      summary.focus({ preventScroll: true });
      summary.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };

    const validateAll = () => {
      const errors = [];
      fieldsToCheck().forEach(el => {
        const message = validateField(el);
        setError(el, message);
        if (message) errors.push({ el, message });
      });
      if (fileInput) {
        const err = errorEl(fileInput);
        if (err && !err.hidden && err.textContent) errors.push({ el: fileInput, message: err.textContent });
      }
      return errors;
    };

    /* Conservation des saisies (mode natif) */
    const saveDraft = () => {
      try {
        const data = {};
        Array.from(form.elements).forEach(el => {
          if (!el.name || el.type === 'hidden' || el.type === 'file' || el.name === '_gotcha') return;
          if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
          data[el.name] = el.value;
        });
        sessionStorage.setItem(storageKey, JSON.stringify(data));
      } catch (e) { /* stockage indisponible : sans conséquence */ }
    };
    const restoreDraft = () => {
      try {
        const data = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        if (!data) return;
        Object.keys(data).forEach(name => {
          form.querySelectorAll(`[name="${CSS.escape(name)}"]`).forEach(el => {
            if (el.type === 'radio' || el.type === 'checkbox') el.checked = el.value === data[name];
            else if (!el.value) el.value = data[name];
          });
        });
        form.querySelectorAll('fieldset[data-group-name]').forEach(g => g.dispatchEvent(new Event('change')));
      } catch (e) { /* rien */ }
    };
    const clearDraft = () => { try { sessionStorage.removeItem(storageKey); } catch (e) { /* rien */ } };

    if (mode === 'native') {
      const focusTarget = form.querySelector('[data-focus-on-load]');
      if (form.querySelector('.notice-error')) restoreDraft();
      if (form.querySelector('[data-quote-success]')) clearDraft();
      if (focusTarget) { focusTarget.focus({ preventScroll: true }); focusTarget.scrollIntoView({ block: 'center' }); }
    }

    /* Envoi */
    const setBusy = (busy, text) => {
      if (!submit) return;
      submit.disabled = busy;
      submit.setAttribute('aria-busy', String(busy));
      if (submitLabel) submitLabel.textContent = busy ? (text || p41t('quote_sending', 'Envoi en cours…')) : submitText;
    };

    window.addEventListener('pageshow', () => setBusy(false));

    const errorFromResponse = (status, data) => {
      const code = data && (data.error || data.code);
      if (status === 429) return p41t('quote_err_429', 'Plusieurs envois ont eu lieu en peu de temps. Patientez quelques secondes puis réessayez.');
      if (status === 413) return p41t('quote_err_413', 'Les fichiers sont trop lourds pour être envoyés. Retirez-en un ou réduisez leur taille.');
      if (status === 400 && typeof code === 'string' && code.includes('EMAIL')) return p41t('quote_err_email', 'L’adresse e-mail n’a pas été acceptée. Vérifiez-la puis réessayez.');
      if (status === 400 && typeof code === 'string' && code.includes('FILE')) return fill(p41t('quote_err_file', 'Un fichier n’a pas été accepté. Vérifiez qu’il s’agit d’une [formats] et sa taille.'), { formats: FORMATS });
      if (status === 400) return p41t('quote_err_400', 'Certaines informations n’ont pas été acceptées. Vérifiez le formulaire puis réessayez.');
      if (status === 401 || status === 403 || status === 404) return p41t('quote_err_unavailable', 'Le service d’envoi est momentanément indisponible.');
      return p41t('quote_err_service', 'L’envoi n’a pas abouti à cause d’une erreur du service d’envoi.');
    };

    form.addEventListener('submit', e => {
      summary.hidden = true;
      if (preparing) { e.preventDefault(); return; }
      const errors = validateAll();
      if (errors.length) {
        e.preventDefault();
        showSummary(errors.length > 1 ? fill(p41t('quote_errors_other', '[count] informations sont à corriger.'), { count: errors.length }) : p41t('quote_errors_one', 'Une information est à corriger.'), errors);
        return;
      }
      if (mode === 'native') { saveDraft(); setBusy(true); return; }

      e.preventDefault();
      const data = new FormData();
      Array.from(form.elements).forEach(el => {
        if (!el.name || el.disabled || el.type === 'file' || el.type === 'submit' || el.type === 'button') return;
        if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
        if (el.closest('[hidden]') && el.name !== '_gotcha') return;
        const value = typeof el.value === 'string' ? el.value.trim() : el.value;
        if (value === '' && el.name !== '_gotcha') return;
        data.append(el.name, value);
      });
      files.forEach(file => data.append(fileName, file, file.name));

      const contact = form.dataset.contactEmail;
      const fallback = ' ' + (contact ? fill(p41t('quote_retry_email', 'Vos informations sont toujours là. Vous pouvez réessayer ou nous écrire à [email].'), { email: contact }) : p41t('quote_retry', 'Vos informations sont toujours là : vous pouvez réessayer.'));
      setBusy(true, files.length ? p41t('quote_sending_files', 'Envoi des fichiers en cours…') : p41t('quote_sending', 'Envoi en cours…'));

      const xhr = new XMLHttpRequest();
      xhr.open('POST', form.action, true);
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.timeout = 120000;
      // Pas d’écouteur de progression d’envoi : il déclencherait une requête CORS
      // préalable (OPTIONS) que le service d’envoi n’accepte pas forcément.
      const fail = message => {
        setBusy(false);
        showSummary(p41t('quote_not_sent', 'La demande n’a pas été envoyée.'), [{ el: null, message: message + fallback }]);
      };
      xhr.onload = () => {
        let json = null;
        try { json = JSON.parse(xhr.responseText); } catch (err) { json = null; }
        const ok = xhr.status >= 200 && xhr.status < 300 && (!json || json.success !== false);
        if (!ok) { fail(errorFromResponse(xhr.status, json)); return; }
        // Succès confirmé par le service : seulement maintenant, on affiche la confirmation.
        const success = form.parentElement.querySelector('[data-quote-success]');
        const nameEl = form.querySelector('[name="fi-sender-fullName"], [name$="-nom"], [name="contact[name]"]');
        const emailEl = form.querySelector('[type="email"]');
        const text = success.querySelector('[data-success-text]');
        if (text && emailEl) {
          const firstName = nameEl && nameEl.value ? nameEl.value.trim().split(' ')[0] : '';
          const thanks = firstName ? fill(p41t('quote_thanks_name', 'Merci [name].'), { name: firstName }) : p41t('quote_thanks', 'Merci.');
          const sent = !files.length ? p41t('quote_sent', 'Votre demande nous a bien été transmise.')
            : fill(files.length > 1 ? p41t('quote_sent_files_other', 'Votre demande nous a bien été transmise avec [count] fichiers.') : p41t('quote_sent_files_one', 'Votre demande nous a bien été transmise avec [count] fichier.'), { count: files.length });
          text.textContent = `${thanks} ${sent} ${fill(p41t('quote_reply_to', 'Nous vous répondrons à l’adresse [email].'), { email: emailEl.value.trim() })}`;
        }
        form.hidden = true;
        success.hidden = false;
        success.focus({ preventScroll: true });
        success.scrollIntoView({ block: 'center' });
        setBusy(false);
      };
      xhr.onerror = () => fail(p41t('quote_conn_lost', 'La connexion a été interrompue.'));
      xhr.ontimeout = () => fail(p41t('quote_timeout', 'L’envoi a pris trop de temps.'));
      xhr.send(data);
    });

    const reset = form.parentElement.querySelector('[data-quote-reset]');
    if (reset) reset.addEventListener('click', () => {
      form.reset();
      files = [];
      renderFiles();
      fieldsToCheck().forEach(el => setError(el, ''));
      form.querySelectorAll('fieldset[data-group-name]').forEach(g => g.dispatchEvent(new Event('change')));
      reset.closest('[data-quote-success]').hidden = true;
      form.hidden = false;
      const first = form.querySelector('input:not([type=hidden]):not([name=_gotcha])');
      if (first) first.focus();
    });
  }

  const boot = scope => scope.querySelectorAll('form[data-quote-form]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document)); else boot(document);
  document.addEventListener('shopify:section:load', e => boot(e.target));
})();
